// A slim strip under the top badge: how each open bet stands in the race on screen,
// and what the last official results did to it.
import { balances, currentBest, type Bet, type BetOutcome } from './bets';
import { CAT_DISPLAY, catIcon } from './cats';
import { formatEuro, ordinal } from './format';
import { name, type HudStore } from './store';
import { TicketIcon } from './TicketIcon';

const MAX_SHOWN = 3;

function newsText(bet: Bet, n: BetOutcome): string {
  const who = bet.participants.find((p) => p.id === n.scorer);
  if (n.settled) {
    const bal = balances(bet);
    const top = Math.max(...Object.values(bal));
    const winners = bet.participants.filter((p) => bal[p.id] === top && top > 0);
    return winners.length === 1 ? `settled · ${winners[0]!.name} wins ${formatEuro(top)}` : 'settled · all square';
  }
  if (!who || !n.scorerCat || n.place === null) return 'no point this race';
  return `${who.name} takes the point · ${name(n.scorerCat)} ${ordinal(n.place)}`;
}

function liveText(bet: Bet, store: HudStore): { text: string; cat?: Bet['participants'][number]['cats'][number] } {
  const snap = store.snapshot.value;
  const score = bet.participants.map((p) => `${p.name} ${bet.points[p.id] ?? 0}`).join(' · ');
  if (!snap || snap.phase === 'starting') return { text: score };
  const best = currentBest(bet, snap);
  if (!best) return { text: score };
  const h2h = bet.mode === 'h2h' && bet.races !== null;
  const where = `${name(best.cat)} ${ordinal(best.place)}`;
  if (h2h) return { text: `${best.owner.name} ahead · ${where}`, cat: best.cat };
  return { text: best.place === 1 ? `${best.owner.name}’s ${name(best.cat)} leads!` : `best: ${best.owner.name}’s ${where}`, cat: best.cat };
}

export function BetTicker({ store }: { store: HudStore }) {
  const bets = store.bets.value;
  const news = store.betNews.value;
  const active = bets.filter((b) => b.status === 'active');
  const withNews = bets.filter((b) => news.some((n) => n.betId === b.id));
  const shown = [...withNews, ...active.filter((b) => !withNews.includes(b))];
  if (!shown.length || store.betsOpen.value) return null;
  const open = () => store.togglePanel('bets');
  if (store.source.value.kind === 'replay' && !withNews.length) {
    return (
      <div class="bet-ticker">
        <button type="button" class="bet-chip quiet" onClick={open}><TicketIcon /> {active.length} bet{active.length === 1 ? '' : 's'} waiting for the live race</button>
      </div>
    );
  }
  return (
    <div class="bet-ticker" aria-label="Bets">
      {shown.slice(0, MAX_SHOWN).map((b) => {
        const n = news.find((x) => x.betId === b.id);
        const live = n ? null : liveText(b, store);
        const cat = n?.scorerCat ?? live?.cat;
        return (
          <button key={b.id} type="button" class={`bet-chip${n ? (n.scorer ? ' news' : ' news none') : ''}`} onClick={open}
            style={cat ? { '--cat': CAT_DISPLAY[cat].colour } : undefined}>
            <span class="t"><TicketIcon /> {b.title}</span>
            {cat && <img src={catIcon(cat)} alt="" width={18} height={18} />}
            <span class="s">{n ? newsText(b, n) : live!.text}</span>
          </button>
        );
      })}
      {shown.length > MAX_SHOWN && <button type="button" class="bet-chip quiet" onClick={open}>+{shown.length - MAX_SHOWN} more</button>}
    </div>
  );
}
